import {
  Alert,
  Button,
  ButtonGroup,
  Divider,
  Drawer,
  Typography,
} from "@mui/material";
import { array, boolean, number, object, string } from "yup";
import { capitalize } from "portal/utils/strings";
import { CheckboxWithLabel, TextField } from "formik-mui";
import { classes, RED_BUTTON } from "portal/theme/theme";
import {
  closestCorners,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { ConfirmationDialog } from "portal/components/ConfirmationDialog";
import { CustomerSelector } from "portal/components/customers/CustomerSelector";
import { FeatureFlag, useFeatureFlag } from "portal/utils/hooks/useFeatureFlag";
import { Field, Form, Formik } from "formik";
import { findWhere, moveItem, sortBy } from "portal/utils/arrays";
import { FleetView, ViewMode, viewModeFromJSON } from "protos/portal/users";
import {
  FleetViewColumn,
  getColumns,
  InfoColumnId,
  isFleetColumnId,
} from "portal/utils/fleetViews";
import { FormikEffect } from "portal/components/FormikEffect";
import {
  ImplementationStatus,
  toImplementationStatus,
} from "portal/utils/robots";
import { isEqual } from "portal/utils/equality";
import { isUndefined } from "portal/utils/identity";
import { MetricHelp } from "portal/components/MetricHelp";
import { MetricInternalIcon } from "portal/components/MetricInternalIcon";
import { Path } from "portal/utils/routing";

import { portalApi } from "portal/state/portalApi";
import { RobotMultiSelector } from "portal/components/robots/RobotMultiSelector";
import { setFleetView, unsetFleetView } from "portal/state/self";
import { skipToken } from "@reduxjs/toolkit/query";
import { SortableContainer } from "portal/components/Sortable";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { StatusSelector } from "portal/components/robots/StatusSelector";
import { TFunction } from "i18next";
import { useCanSeeOtherCustomers } from "portal/views/customers/permissions";
import { useDispatch } from "react-redux";
import { useJigsawMetrics } from "../../utils/hooks/useJigsawMetrics";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router-dom";
import { userApi } from "../../../../common/src/state/portalApi/user";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { values } from "portal/utils/objects";
import AddIcon from "@mui/icons-material/AddOutlined";
import CardsIcon from "@mui/icons-material/ViewModuleOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import React, { FunctionComponent, useMemo, useState } from "react";
import RemoveIcon from "@mui/icons-material/RemoveOutlined";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import TableIcon from "@mui/icons-material/TableChartOutlined";

interface Props {
  view: FleetView;
  open?: boolean;
  onChange: (editingView: FleetView) => void;
  onSave: (editingView: FleetView) => void;
  onCancel: () => void;
}

export const FleetViewEditor: FunctionComponent<Props> = ({
  view,
  open = false,
  onChange,
  onSave,
  onCancel,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isInternal, user, customer, measurementSystem } = useSelf();
  const { isEnabled: hasUnvalidatedMetrics } = useFeatureFlag(
    FeatureFlag.UNVALIDATED_METRICS
  );
  const { isEnabled: includePlantProfile } = useFeatureFlag(
    FeatureFlag.CATEGORY_COLLECTION
  );
  const { i18n, t } = useTranslation();
  const useJigsaw = useJigsawMetrics();

  const canReadCustomers = useCanSeeOtherCustomers();

  const [deleteFleetView] = useMutationPopups(
    userApi.endpoints.deleteFleetView.useMutation(),
    {
      success: t("utils.actions.deletedLong", {
        subject: capitalize(t("views.fleet.views.fleetView_one")),
      }),
    }
  );
  // customers are selected by uuid; this list (already fetched by the
  // CustomerSelector below) resolves the matching numeric ids for consumers
  // that still work with ids
  const { data: allCustomers } = portalApi.endpoints.listCustomers.useQuery(
    canReadCustomers ? undefined : skipToken
  );

  const [updateFleetView] = useMutationPopups(
    userApi.endpoints.updateFleetView.useMutation(),
    {
      success: capitalize(
        t("utils.actions.savedLong", {
          subject: capitalize(t("views.fleet.views.fleetView_one")),
        })
      ),
    }
  );
  // confirmation dialog
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const columns = useMemo(
    () =>
      user
        ? getColumns(
            i18n,
            t,
            measurementSystem,
            useJigsaw,
            includePlantProfile === true
          )
        : [],
    [i18n, includePlantProfile, measurementSystem, t, useJigsaw, user]
  );

  // columns
  const visibleColumns = useMemo<FleetViewColumn[]>(
    () =>
      view.columns
        .map((id) => findWhere(columns, { id }))
        .filter((column) => !isUndefined(column)),
    [columns, view.columns]
  );
  const hiddenColumns = useMemo<FleetViewColumn[]>(
    () => columns.filter((column) => !visibleColumns.includes(column)),
    [columns, visibleColumns]
  );

  const [draggingId, setDraggingId] = useState<string | undefined>(undefined);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const setList = (list: FleetViewColumn[]): void => {
    onChange({ ...view, columns: list.map((column) => column.id) });
  };

  const handleDragStart = ({ active }: DragStartEvent): void => {
    if (!isFleetColumnId(active.id)) {
      return;
    }
    setDraggingId(active.id);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    setList(
      // offset indexes because of group column
      moveItem(
        visibleColumns,
        active.data.current?.sortable.index + 1,
        over?.data.current?.sortable.index + 1
      )
    );

    setDraggingId(undefined);
  };

  const getRenderColumn =
    (
      t: TFunction,
      setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean
      ) => Promise<any>,
      viewMode: ViewMode,
      isHidden: boolean = false
    ) =>
    (column: FleetViewColumn): JSX.Element => {
      if (isHidden && column.isPending && !hasUnvalidatedMetrics) {
        return <></>;
      }
      if (column.isInternal && !isInternal) {
        return <></>;
      }
      return (
        <li
          key={column.id}
          className={classes(
            "group whitespace-nowrap flex items-center rounded-sm",
            {
              "opacity-20":
                viewMode === viewModeFromJSON(ViewMode.VIEW_MODE_CARDS) &&
                !column.renderForCard,
              "bg-slate-600": column.id === draggingId,
            }
          )}
        >
          <button
            type="button"
            className="w-full flex items-center whitespace-nowrap border-0 bg-transparent text-inherit p-0"
            onClick={() => {
              if (
                viewMode === viewModeFromJSON(ViewMode.VIEW_MODE_CARDS) &&
                !column.renderForCard
              ) {
                return;
              }
              const newColumns = isHidden
                ? [...visibleColumns, column]
                : visibleColumns.filter(({ id }) => id !== column.id);
              setFieldValue(
                "columns",
                newColumns.map(({ id }) => id)
              );
            }}
          >
            {(() => {
              if (
                viewMode === viewModeFromJSON(ViewMode.VIEW_MODE_CARDS) &&
                !column.renderForCard
              ) {
                return <InfoIcon className="cursor-not-allowed" />;
              }
              return isHidden ? (
                <AddIcon className="cursor-pointer hover:text-green-700" />
              ) : (
                <RemoveIcon className="cursor-pointer hover:text-red-700" />
              );
            })()}
            {column.isInternal && <MetricInternalIcon className="ml-1" />}
            <strong
              className={classes("text-ellipsis ml-1", {
                "cursor-row-resize": !isHidden,
                "cursor-pointer": isHidden,
              })}
            >
              {column.headerName}
            </strong>
            <span className="inline-block grow" /* spacer */ />
            {column.metric && (
              <MetricHelp metric={column.metric} className="text-gray-300" />
            )}
          </button>
        </li>
      );
    };

  if (!open) {
    return;
  }

  return (
    <Drawer
      variant="permanent"
      anchor="right"
      classes={{
        paper: classes("h-auto p-4 bottom-0", "top-16", "w-screen md:w-80"),
      }}
    >
      <Formik
        enableReinitialize
        initialValues={{
          name: view.name,
          columns: view.columns,
          search: view.search,
          showInternal: view.showInternal,
          showMap: view.showMap,
          showOffline: view.showOffline,
          statuses: view.statuses,
          pinnedRobotIds: view.pinnedRobotIds,
          customerIds: (() => {
            if (canReadCustomers) {
              return view.customerIds;
            } else {
              return customer?.db?.id ? [customer.db.id] : [];
            }
          })(),
          customerUuids: (() =>
            canReadCustomers
              ? view.customerUuids
              : customer?.uuid
              ? [customer.uuid]
              : [])(),
          viewMode: view.viewMode,
        }}
        validationSchema={object({
          name: string().required(t("utils.form.required")),
          columns: array(string()).required(t("utils.form.required")),
          search: string(),
          showInternal: boolean(),
          showMap: boolean(),
          showOffline: boolean(),
          statuses: array(
            string()
              .oneOf<ImplementationStatus>(values(ImplementationStatus))
              .required()
          )
            .ensure()
            .required(t("utils.form.required")),
          pinnedRobotIds: array()
            .of(number())
            .required(t("utils.form.required")),
          customerIds: array().of(number()),
          customerUuids: array().of(string()),
          viewMode: number().oneOf(values(ViewMode)),
        })}
        onSubmit={async (values) => {
          if (!user?.userId) {
            return;
          }
          // values carry matched customerIds/customerUuids, so the backend
          // resolves the same customers from either field
          const result = { ...view, ...values };
          await updateFleetView({ userId: user.userId, fleetView: result });
          dispatch(setFleetView(result));
          onSave(result);
        }}
      >
        {({ submitForm, isSubmitting, values, setFieldValue }) => (
          <Form className={classes("flex flex-col gap-4")}>
            <FormikEffect
              onChange={(previous, next) => {
                if (
                  !previous?.values ||
                  !next.values ||
                  isEqual(previous.values, next.values)
                ) {
                  return;
                }
                onChange({
                  ...view,
                  ...next.values,
                });
              }}
            />
            <div className="flex justify-end gap-2">
              <Button
                {...RED_BUTTON}
                onClick={() => setConfirmDelete(true)}
                startIcon={<DeleteIcon />}
              >
                {t("utils.actions.delete")}
              </Button>
              {confirmDelete && (
                <ConfirmationDialog
                  title={t("utils.actions.deleteLong", {
                    subject: t("views.fleet.views.fleetView_one"),
                  })}
                  description={t(
                    "components.ConfirmationDialog.delete.description",
                    {
                      subject: view.name,
                    }
                  )}
                  destructive
                  yesText={t("utils.actions.delete", {
                    subject: view.name,
                  })}
                  onClose={() => setConfirmDelete(false)}
                  yesDisabled={!user?.userId || !view.db?.id}
                  onYes={async () => {
                    if (!user?.userId || !view.db?.id) {
                      return;
                    }
                    await deleteFleetView({
                      userId: user.userId,
                      fleetViewId: view.db.id,
                    });
                    dispatch(unsetFleetView(view.db.id));
                    navigate(Path.FLEET);
                  }}
                />
              )}
              <Button color="inherit" variant="text" onClick={() => onCancel()}>
                {t("utils.actions.cancel")}
              </Button>
              <Button
                color="secondary"
                variant="contained"
                disabled={!view}
                loading={isSubmitting}
                onClick={submitForm}
                startIcon={<SaveIcon />}
              >
                {t("utils.actions.save")}
              </Button>
            </div>
            <Field
              component={TextField}
              label={t("views.fleet.views.fields.name")}
              name="name"
              className="text-lg"
            />
            {/* Hide this until we can figure out how to do it in a not confusing way... */}
            {/* <Field
              {...SMALL_TEXT_FIELD_DARK}
              component={TextField}
              label={t("utils.actions.search")}
              name="search"
              className="text-lg"
            /> */}
            {canReadCustomers && (
              <CustomerSelector
                valueKey="uuid"
                value={values.customerUuids}
                multiple
                onChange={(customerUuids) => {
                  setFieldValue("customerUuids", customerUuids ?? []);
                  setFieldValue(
                    "customerIds",
                    (customerUuids ?? [])
                      .map(
                        (customerUuid) =>
                          allCustomers?.find(
                            ({ uuid }) => uuid === customerUuid
                          )?.db?.id
                      )
                      .filter((id) => !isUndefined(id))
                  );
                }}
                small
              />
            )}
            <RobotMultiSelector
              selectedRobots={values.pinnedRobotIds}
              label={t("views.fleet.views.fields.pinnedRobotIds")}
              customerIds={values.customerIds}
              onChange={(pinnedRobotIds) => {
                setFieldValue("pinnedRobotIds", pinnedRobotIds);
              }}
            />
            <StatusSelector
              value={values.statuses.map((status) =>
                toImplementationStatus(status)
              )}
              onChange={(statuses) => {
                setFieldValue("statuses", statuses);
              }}
            />
            <Divider />
            <Field
              type="checkbox"
              component={CheckboxWithLabel}
              name="showMap"
              Label={{
                label: t("utils.actions.showLong", {
                  subject: t("components.map.map"),
                }),
              }}
            />
            <Field
              type="checkbox"
              component={CheckboxWithLabel}
              name="showOffline"
              Label={{
                label: t("utils.actions.showLong", {
                  subject: t("utils.descriptors.offline"),
                }),
              }}
            />
            {isInternal && (
              <Field
                type="checkbox"
                component={CheckboxWithLabel}
                name="showInternal"
                Label={{
                  label: t("utils.actions.showLong", {
                    subject: t("views.fleet.robots.toggleable.internal"),
                  }),
                }}
              />
            )}
            <Divider />
            <ButtonGroup variant="contained" className="w-full flex">
              <Button
                variant={
                  values.viewMode === viewModeFromJSON(ViewMode.VIEW_MODE_CARDS)
                    ? "contained"
                    : "outlined"
                }
                color={
                  values.viewMode === viewModeFromJSON(ViewMode.VIEW_MODE_CARDS)
                    ? "primary"
                    : "inherit"
                }
                classes={{ root: "w-1/2" }}
                onClick={() => {
                  setFieldValue(
                    "viewMode",
                    viewModeFromJSON(ViewMode.VIEW_MODE_CARDS)
                  );
                }}
                startIcon={<CardsIcon />}
              >
                {t("views.fleet.views.fields.viewMode.values.cards")}
              </Button>
              <Button
                variant={
                  values.viewMode === viewModeFromJSON(ViewMode.VIEW_MODE_TABLE)
                    ? "contained"
                    : "outlined"
                }
                color={
                  values.viewMode === viewModeFromJSON(ViewMode.VIEW_MODE_TABLE)
                    ? "primary"
                    : "inherit"
                }
                classes={{ root: "w-1/2" }}
                onClick={() => {
                  setFieldValue(
                    "viewMode",
                    viewModeFromJSON(ViewMode.VIEW_MODE_TABLE)
                  );
                }}
                startIcon={<TableIcon />}
              >
                {t("views.fleet.views.fields.viewMode.values.table")}
              </Button>
            </ButtonGroup>
            <Divider />
            {values.viewMode === ViewMode.VIEW_MODE_CARDS && (
              <Alert severity="info" variant="outlined" color="info">
                {t("views.fleet.views.tableOnly")}
              </Alert>
            )}
            <Typography variant="subtitle2">
              {t("views.reports.scheduled.editor.columnsVisible")}
            </Typography>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContainer
                id="visible"
                // NOTE: except for the group column, the items in this
                // container must match the set of items reordered in
                // handleDragEnd or else drag and drop breaks.
                items={visibleColumns.filter(
                  (column) => column.id !== InfoColumnId.GROUP
                )}
                render={getRenderColumn(
                  t,
                  setFieldValue,
                  values.viewMode,
                  false
                )}
              />
            </DndContext>
            <Typography variant="subtitle2">
              {t("views.reports.scheduled.editor.columnsHidden")}
            </Typography>
            <ul className="p-0 m-0">
              {sortBy(hiddenColumns, "headerName").map(
                getRenderColumn(t, setFieldValue, values.viewMode, true)
              )}
            </ul>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};
